export const MODERN_FRAME_PROTOCOL = 'modern-frame' as const;
export const MODERN_FRAME_VERSION = 2 as const;
export const MODERN_FRAME_SUPPORTED_VERSIONS = [MODERN_FRAME_VERSION] as const;

export const MAX_MODERN_FRAME_IDENTIFIER_LENGTH = 128;
export const MAX_MODERN_FRAME_TITLE_LENGTH = 256;
export const MAX_MODERN_FRAME_ERROR_MESSAGE_LENGTH = 512;

interface ModernFrameMessageBaseV2 {
  readonly protocol: typeof MODERN_FRAME_PROTOCOL;
  readonly version: typeof MODERN_FRAME_VERSION;
}

interface ModernFrameChannelMessageBaseV2 extends ModernFrameMessageBaseV2 {
  readonly frameInstanceId: string;
  readonly hostSessionId: string;
}

export type ModernFrameAccessResultV2 =
  | 'allowed'
  | 'denied'
  | 'auth-required'
  | 'unavailable';

export type ModernFrameErrorScopeV2 =
  | 'document'
  | 'handshake'
  | 'navigation'
  | 'authorization'
  | 'frame';

export type ModernFrameNavigationTriggerV2 = 'imperative' | 'popstate' | 'hashchange';

export interface ModernFrameUserContextV2 {
  readonly userId: number;
  readonly displayName: string;
  readonly email: string;
  readonly profiles: readonly string[];
}

/** First, unauthenticated window-level message emitted by a new iframe document. */
export interface ModernFrameHelloMessageV2 extends ModernFrameMessageBaseV2 {
  readonly type: 'FRAME_HELLO';
  readonly frameInstanceId: string;
  readonly supportedVersions: readonly number[];
}

/** Window-level response whose transferred MessagePort becomes the session channel. */
export interface ModernFrameHostConnectMessageV2 extends ModernFrameMessageBaseV2 {
  readonly type: 'HOST_CONNECT';
  readonly frameInstanceId: string;
  readonly hostSessionId: string;
}

export type ModernFrameWindowMessageV2 =
  | ModernFrameHelloMessageV2
  | ModernFrameHostConnectMessageV2;

export interface ModernFrameChannelReadyMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'CHANNEL_READY';
}

export interface ModernFrameSessionContextMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'SESSION_CONTEXT';
  readonly payload: { readonly user: ModernFrameUserContextV2 };
}

export interface ModernFrameLogoutMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'LOGOUT';
  readonly payload: { readonly reason?: string };
}

export interface ModernFrameActivateRouteMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'ACTIVATE_ROUTE';
  readonly navigationId: string;
  readonly payload: {
    readonly canonicalPath: string;
    readonly viewportInsets?: { readonly top: number };
    readonly trigger?: ModernFrameNavigationTriggerV2;
    readonly replace?: boolean;
  };
}

export interface ModernFrameRouteCommittedMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'ROUTE_COMMITTED';
  readonly navigationId: string;
  readonly payload: { readonly path: string };
}

export interface ModernFrameRouteFailedMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'ROUTE_FAILED';
  readonly navigationId: string;
  readonly payload: {
    readonly path: string;
    readonly code: string;
  };
}

export interface ModernFrameDeactivateRouteMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'DEACTIVATE_ROUTE';
  readonly navigationId: string;
}

export interface ModernFrameRouteDeactivatedMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'ROUTE_DEACTIVATED';
  readonly navigationId: string;
}

export interface ModernFrameAuthorizeRouteRequestMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'AUTHORIZE_ROUTE_REQUEST';
  readonly requestId: string;
  readonly payload: { readonly canonicalRoute: string };
}

export interface ModernFrameAuthorizeRouteResultMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'AUTHORIZE_ROUTE_RESULT';
  readonly requestId: string;
  readonly payload: { readonly result: ModernFrameAccessResultV2 };
}

export interface ModernFrameTokenRefreshRequestMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'TOKEN_REFRESH_REQUEST';
  readonly requestId: string;
}

export interface ModernFrameTokenRefreshResultMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'TOKEN_REFRESH_RESULT';
  readonly requestId: string;
  readonly payload: { readonly success: boolean };
}

/** Requests that the shell make an already-authorized modern URL canonical in its history. */
export interface ModernFrameNavigateRequestMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'NAVIGATE_REQUEST';
  readonly navigationId: string;
  readonly payload: {
    readonly path: string;
    readonly replace?: boolean;
    readonly trigger?: ModernFrameNavigationTriggerV2;
  };
}

/** Requests that the shell navigate to a legacy (non-modern) route and deactivate the frame. */
export interface ModernFrameShellNavigateRequestMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'SHELL_NAVIGATE_REQUEST';
  readonly navigationId: string;
  readonly payload: {
    readonly path: string;
    readonly replace?: boolean;
  };
}

export interface ModernFrameTitleChangedMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'TITLE_CHANGED';
  readonly navigationId: string;
  readonly payload: { readonly title: string };
}

export interface ModernFrameOverlayChangedMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'OVERLAY_CHANGED';
  readonly payload: {
    readonly overlayId: string;
    readonly open: boolean;
  };
}

/** Confirms that the shell host is promoted before the modern overlay is rendered. */
export interface ModernFrameOverlayReadyMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'OVERLAY_READY';
  readonly payload: { readonly overlayId: string };
}

export interface ModernFrameAuthRequiredMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'AUTH_REQUIRED';
  readonly payload: { readonly reason?: string };
}

export interface ModernFrameUserActivityMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'USER_ACTIVITY';
  readonly payload: { readonly timestamp: number };
}

export interface ModernFrameErrorMessageV2 extends ModernFrameChannelMessageBaseV2 {
  readonly type: 'FRAME_ERROR';
  readonly requestId?: string;
  readonly navigationId?: string;
  readonly payload: {
    readonly code: string;
    readonly scope: ModernFrameErrorScopeV2;
    readonly message: string;
    readonly recoverable: boolean;
  };
}

export type HostToModernFrameMessageV2 =
  | ModernFrameSessionContextMessageV2
  | ModernFrameLogoutMessageV2
  | ModernFrameActivateRouteMessageV2
  | ModernFrameDeactivateRouteMessageV2
  | ModernFrameAuthorizeRouteResultMessageV2
  | ModernFrameTokenRefreshResultMessageV2
  | ModernFrameOverlayReadyMessageV2;

export type ModernFrameToHostMessageV2 =
  | ModernFrameChannelReadyMessageV2
  | ModernFrameRouteCommittedMessageV2
  | ModernFrameRouteFailedMessageV2
  | ModernFrameRouteDeactivatedMessageV2
  | ModernFrameAuthorizeRouteRequestMessageV2
  | ModernFrameTokenRefreshRequestMessageV2
  | ModernFrameNavigateRequestMessageV2
  | ModernFrameShellNavigateRequestMessageV2
  | ModernFrameTitleChangedMessageV2
  | ModernFrameOverlayChangedMessageV2
  | ModernFrameAuthRequiredMessageV2
  | ModernFrameUserActivityMessageV2
  | ModernFrameErrorMessageV2;

export type ModernFrameChannelMessageV2 =
  | HostToModernFrameMessageV2
  | ModernFrameToHostMessageV2;

export type ModernFrameMessageV2 =
  | ModernFrameWindowMessageV2
  | ModernFrameChannelMessageV2;

type ModernFrameCrypto = Pick<Crypto, 'getRandomValues'> &
  Partial<Pick<Crypto, 'randomUUID'>>;

/** Creates a cryptographically random identifier without an insecure fallback. */
export function createModernFrameId(cryptoSource: ModernFrameCrypto = globalThis.crypto): string {
  if (typeof cryptoSource?.randomUUID === 'function') {
    return cryptoSource.randomUUID();
  }

  if (typeof cryptoSource?.getRandomValues !== 'function') {
    throw new Error('A cryptographically secure random source is required.');
  }

  const bytes = cryptoSource.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
